import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { Icon, type IconName } from "./Icon";
import "./option-button.css";

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-pressed" | "aria-current"> & {
  /** La riga principale (il nome del ruolo, dell'azienda, del Lead). */
  label: ReactNode;
  /** La riga piccola sotto, in grigio (il ruolo, la città, quante persone). */
  detail?: ReactNode;
  /** Un'icona prima del nome (la persona nel «Vedi come»). */
  icon?: IconName;
  /** La riga piccola accanto al nome invece che sotto. */
  inline?: boolean;
  /** Qualcosa accanto al nome, per esempio un `Chip` («di sistema»). */
  aside?: ReactNode;
  /** La riga scelta: si vede con bordo o segno d'accento e lo dice agli screen reader. */
  selected?: boolean;
  /** Come lo dice: `pressed` (`aria-pressed`, una scelta in un elenco che resta sulla pagina) o `current`
      (`aria-current`, l'elemento in cui si è già, come l'azienda aperta). */
  selectedAs?: "pressed" | "current";
  /** `boxed`: riquadro con bordo (Ruoli, scelta dell'azienda, nuovo affare). `plain`: senza bordo, in un menu. */
  look?: "boxed" | "plain";
  /** Arriva al `<button>` (in React 19 `ref` è una prop). */
  ref?: Ref<HTMLButtonElement>;
};

// La riga cliccabile di un elenco di scelte: tutta la riga è un pulsante, con un nome e una riga piccola sotto.
export function OptionButton({
  label,
  detail,
  aside,
  icon,
  inline = false,
  selected,
  selectedAs = "pressed",
  look = "boxed",
  type = "button",
  className,
  ...rest
}: Props) {
  const state =
    selectedAs === "pressed"
      ? { "aria-pressed": selected === undefined ? undefined : selected }
      : { "aria-current": selected ? ("true" as const) : undefined };
  return (
    <button type={type} className={className ? `g-option ${className}` : "g-option"} data-look={look} data-inline={inline ? "true" : undefined} {...state} {...rest}>
      {icon ? <Icon name={icon} /> : null}
      <span className="g-option__text">
        <span className="g-option__main">
          <span className="g-option__label">{label}</span>
          {aside}
        </span>
        {detail === undefined || detail === null ? null : (
          <>
            {" "}
            <span className="g-option__detail">{detail}</span>
          </>
        )}
      </span>
    </button>
  );
}
